import type {
    LineEndT,
    LineI,
    LineStrokeAlignT,
    NumberInputT,
    SelectInputT,
    SimpleStyleT,
    StyleUnitT,
} from "tweenly"
import { LINE_STROKE_ALIGNS } from "tweenly"
import Box from "@mui/material/Box"
import { MAX_INPUT_NUM } from "../../../../constants"
import {
    LINE_STYLE_PRESETS,
    LINE_WEIGHT_UNITS,
    LineStylePresetT,
    applyLineStylePreset,
    getDashPattern,
    getLineStylePreset,
    setLineStrokeAlign,
    setLineWeightUnit,
} from "../../../../helpers/line.helpers"
import { useAppDispatch } from "../../../../hooks/useRedux"
import { updateObjectsPositionAction } from "../../../../store/slices/objects.slice"
import ExpandableSection from "../../../common/ExpandableSection"
import CompactSelect from "../../../common/PropertyTools/CompactSelect"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import LineEndSelect from "./LineEndSelect"

interface Props {
    object: LineI
    handleSetStyleValue: (property: any, value: any) => void
    handleSetObjectValue: (property: any, value: any) => void
}

const dashInput: NumberInputT = {
    type: "number",
    props: { inputProps: { min: 0, max: MAX_INPUT_NUM, step: 1 } },
}

/** Weight (px or pt), dash pattern and caps of a line — Figma's stroke section for a line. */
const LineStroke = ({ object, handleSetStyleValue, handleSetObjectValue }: Props) => {
    const dispatch = useAppDispatch()
    const findStyle = (property: string): SimpleStyleT | undefined =>
        object.styles.find((style) => style.property === property)
    const lineWeight = findStyle("lineWeight")
    const strokeLinecap = findStyle("strokeLinecap")
    const strokeDasharray = findStyle("strokeDasharray")

    if (!lineWeight || !strokeLinecap || !strokeDasharray) return <></>

    const [dash, gap] = getDashPattern(object.styles)
    const preset = getLineStylePreset([dash, gap])

    // dash and gap are the two halves of one style, edited as if they were styles of their own
    const dashStyle: SimpleStyleT = {
        ...strokeDasharray,
        property: "dash",
        value: dash,
        unit: "px",
        input: { ...dashInput, label: "dash" },
    }
    const gapStyle: SimpleStyleT = {
        ...strokeDasharray,
        property: "gap",
        value: gap,
        unit: "px",
        input: { ...dashInput, label: "gap" },
    }

    const handleStyleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleSetStyleValue(e.target.name, e.target.value)
    // an emptied field is not 0 — it is left as it was
    const isEmpty = (e: React.ChangeEvent<HTMLInputElement>) => String(e.target.value).trim() === ""
    const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isEmpty(e)) handleSetStyleValue("lineWeight", Number(e.target.value))
    }
    const handleDashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isEmpty(e)) return
        const value = Math.max(0, Number(e.target.value) || 0)
        handleSetStyleValue(
            "strokeDasharray",
            e.target.name === "dash" ? [value, gap] : [dash, value]
        )
    }
    const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
        handleSetObjectValue(
            "styles",
            setLineWeightUnit(object.styles, e.target.value as StyleUnitT)
        )
    const handleEndChange = (which: "startPoint" | "endPoint") => (value: LineEndT) =>
        handleSetObjectValue(which, value)
    // the end points stay put and the box moves, in one undo step with the new position
    const handleAlignChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const strokeAlign = e.target.value as LineStrokeAlignT
        dispatch(
            updateObjectsPositionAction([
                { object, property: "styles", value: setLineStrokeAlign(object, strokeAlign) },
                { object, property: "strokeAlign", value: strokeAlign },
            ])
        )
    }
    const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
        handleSetObjectValue(
            "styles",
            applyLineStylePreset(object.styles, e.target.value as LineStylePresetT)
        )

    const options = (values: readonly string[]) => values.map((value) => ({ value, label: value }))
    const half = (child: React.ReactNode) => (
        <Box flex={1} minWidth={0}>
            {child}
        </Box>
    )

    return (
        <ExpandableSection
            expanded={true}
            title={"Stroke"}
            component={
                // two to a row, like every other section; the end point pickers fill their
                // column (the theme gives each form control a 3px margin)
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    width="100%"
                    pb={0.5}
                    sx={{ "& .MuiFormControl-root": { m: 0, width: "100%" } }}
                >
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            {half(
                                <CompactSelect
                                    name="strokeAlign"
                                    value={object.strokeAlign ?? "center"}
                                    options={options(LINE_STROKE_ALIGNS)}
                                    label="position"
                                    tooltip="Stroke position"
                                    handleOnChange={handleAlignChange}
                                />
                            )}
                            <Box flex={1} minWidth={0} display="flex" gap="3px">
                                <Box flex={1} minWidth={0}>
                                    <PrefixedInput
                                        name="lineWeight"
                                        value={lineWeight.value}
                                        label="weight"
                                        tooltip={`Weight (${lineWeight.unit ?? "px"})`}
                                        handleOnChange={handleWeightChange}
                                        min={0}
                                    />
                                </Box>
                                <Box width={64} flexShrink={0}>
                                    <CompactSelect
                                        name="lineWeightUnit"
                                        value={lineWeight.unit}
                                        options={options(LINE_WEIGHT_UNITS)}
                                        label="unit"
                                        tooltip="Weight unit"
                                        handleOnChange={handleUnitChange}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </PropertyRow>
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            {half(
                                <CompactSelect
                                    name="lineStyle"
                                    value={preset}
                                    options={options(LINE_STYLE_PRESETS)}
                                    label="style"
                                    tooltip="Stroke style"
                                    handleOnChange={handlePresetChange}
                                />
                            )}
                            {half(
                                <CompactSelect
                                    name={strokeLinecap.property}
                                    value={strokeLinecap.value}
                                    options={options(
                                        (strokeLinecap.input as SelectInputT).props as string[]
                                    )}
                                    label={strokeLinecap.input.label ?? "caps"}
                                    tooltip="Caps"
                                    handleOnChange={handleStyleChange}
                                />
                            )}
                        </Box>
                    </PropertyRow>
                    {preset !== "solid" && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                {half(
                                    <PrefixedInput
                                        name="dash"
                                        value={dashStyle.value}
                                        label="dash (px)"
                                        tooltip="Dash (px)"
                                        handleOnChange={handleDashChange}
                                        min={0}
                                    />
                                )}
                                {half(
                                    <PrefixedInput
                                        name="gap"
                                        value={gapStyle.value}
                                        label="gap (px)"
                                        tooltip="Gap (px)"
                                        handleOnChange={handleDashChange}
                                        min={0}
                                    />
                                )}
                            </Box>
                        </PropertyRow>
                    )}
                    <PropertyRow>
                        <Box display="flex" gap={1}>
                            {half(
                                <LineEndSelect
                                    which="start"
                                    value={object.startPoint}
                                    onChange={handleEndChange("startPoint")}
                                />
                            )}
                            {half(
                                <LineEndSelect
                                    which="end"
                                    value={object.endPoint}
                                    onChange={handleEndChange("endPoint")}
                                />
                            )}
                        </Box>
                    </PropertyRow>
                </Box>
            }
        />
    )
}

export default LineStroke
